import { useAuth } from '@/auth/useAuth';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface JobLog {
  id: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  timestamp: string;
  payload?: unknown;
  /** Desde dónde se generó el log. Los logs anteriores a este campo no lo traen. */
  origen?: {
    servidor: string | null;
    entorno: string | null;
    version: string | null;
    hostname: string | null;
  };
}

export interface BackgroundJobOptions {
  service?: string;
  limit?: number;
}

export interface ProgressInfo {
  current: number;
  total: number;
  description?: string;
}

export interface UseBackgroundJobResult {
  isProcessing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  progress: Record<string, ProgressInfo>;
  /** Datos estructurados que el backend manda al terminar (métricas del proceso), si los envía. */
  summary: unknown;
  logs: JobLog[];
  startJob: (payload: Record<string, unknown>) => Promise<void>;
  connectOnly: () => void;
  loadMoreLogs: () => Promise<void>;
  cancelObservation: () => void;
}

export function useBackgroundJob(enqueueUrl: string, streamUrl: string, options: BackgroundJobOptions = {}): UseBackgroundJobResult {
  const { token: rawToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [progress, setProgress] = useState<Record<string, ProgressInfo>>({});
  const [summary, setSummary] = useState<unknown>(null);

  const [logs, setLogs] = useState<JobLog[]>([]);

  const eventSourceRef = useRef<EventSource | null>(null);

  // Normalizar token una sola vez para evitar "Bearer Bearer" o "undefined" en cualquier parte del hook
  const cleanToken = (typeof rawToken === 'string' ? rawToken : '').replace(/^Bearer\s+/i, '').trim();

  const startListeningToLogs = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    if (!cleanToken || cleanToken === 'undefined') {
      setLogs((prev) => [
        {
          id: 'error-auth',
          level: 'error',
          message: 'Token de autenticación inválido o ausente',
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ]);
      return;
    }

    // Construcción de la URL con parámetros de filtrado y token
    const baseStreamUrl = `${API_URL}${streamUrl}`;
    const url = new URL(baseStreamUrl, window.location.origin);

    if (options.service) url.searchParams.append('service', options.service);
    if (options.limit) url.searchParams.append('limit', options.limit.toString());
    url.searchParams.append('token', cleanToken);

    const source = new EventSource(url.toString());
    eventSourceRef.current = source;
    setIsProcessing(true);

    source.addEventListener('log', (event: MessageEvent) => {
      try {
        const data: JobLog = JSON.parse(event.data);
        setLogs((prev) => {
          // Evitar duplicados por si el SSE manda algo que ya cargamos via history
          if (prev.some((l) => l.id === data.id)) return prev;
          return [data, ...prev];
        });
      } catch (e) {
        console.error('Error parseando log', e);
      }
    });

    source.addEventListener('progress', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const pId = data.progressId || 'default';
        setProgress((prev) => ({
          ...prev,
          [pId]: {
            current: data.current,
            total: data.total,
            description: data.description,
          },
        }));
      } catch (e) {
        console.error('Error parseando progreso', e);
      }
    });

    source.addEventListener('done', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const pId = data.progressId || 'default';

        if (data.resumen) setSummary(data.resumen);

        setLogs((prev) => [
          {
            id: crypto.randomUUID(),
            level: 'success',
            message: data.message || 'Trabajo finalizado',
            timestamp: new Date().toISOString(),
          },
          ...prev,
        ]);

        // Eliminar el progreso de este ID específico
        setProgress((prev) => {
          const next = { ...prev };
          delete next[pId];
          return next;
        });
      } catch (_e) {
        // Ignorar JSON malformado al finalizar el event stream
      }
      setIsProcessing(false);
    });

    source.onerror = (error) => {
      console.error('SSE Error:', error);
    };
  }, [streamUrl, cleanToken, options.service, options.limit]);

  const loadMoreLogs = async () => {
    if (isLoadingMore || !hasMore || !cleanToken || cleanToken === 'undefined') return;

    try {
      setIsLoadingMore(true);

      const oldestLog = logs[logs.length - 1];
      if (!oldestLog) {
        setIsLoadingMore(false);
        return;
      }

      const beforeTimestamp = new Date(oldestLog.timestamp).getTime();
      const historyUrl = `${API_URL}/api/system-jobs/history`;
      const url = new URL(historyUrl, window.location.origin);

      if (options.service) url.searchParams.append('service', options.service);
      url.searchParams.append('limit', '50');
      url.searchParams.append('before', beforeTimestamp.toString());

      const res = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const newLogs: JobLog[] = data.logs;

        if (newLogs.length === 0) {
          setHasMore(false);
        } else {
          setLogs((prev) => {
            const filteredNewLogs = newLogs.filter((n) => !prev.some((p) => p.id === n.id));

            if (filteredNewLogs.length === 0) {
              setHasMore(false);
              return prev;
            }

            return [...prev, ...filteredNewLogs];
          });
        }
      }
    } catch (error) {
      console.error('Error cargando historial de logs:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const startJob = async (payload: Record<string, unknown>) => {
    if (!cleanToken || cleanToken === 'undefined') return;

    try {
      setIsProcessing(true);
      setLogs([]);
      setHasMore(true);
      setProgress({});
      setSummary(null);

      const baseEnqueueUrl = `${API_URL}${enqueueUrl}`;
      const url = new URL(baseEnqueueUrl, window.location.origin);
      url.searchParams.append('token', cleanToken);

      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${cleanToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        startListeningToLogs();
      } else {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.error || 'Falló el encolado del trabajo');
      }
    } catch (error) {
      console.error(error);
      setIsProcessing(false);
      setLogs([
        {
          id: 'error-start',
          level: 'error',
          message: error instanceof Error ? error.message : 'Error desconocido',
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const connectOnly = useCallback(() => {
    setLogs([]);
    setHasMore(true);
    setProgress({});
    startListeningToLogs();
  }, [startListeningToLogs]);

  const cancelObservation = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    isProcessing,
    isLoadingMore,
    hasMore,
    progress,
    summary,
    logs,
    startJob,
    connectOnly,
    loadMoreLogs,
    cancelObservation,
  };
}
